import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { Save, Eye, Mail, ArrowLeft } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  variables: string[];
  lastUpdated: Date;
}

const DEFAULT_TEMPLATES = [
  {
    name: 'RSVP Confirmation',
    subject: 'RSVP Confirmed: {{eventName}}',
    htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background-color: #1E3A5F; color: #fff; padding: 20px; text-align: center; }
    .content { background-color: #f9f9f9; padding: 30px; border: 1px solid #ddd; }
    .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
    h1 { margin: 0; font-size: 24px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>RSVP Confirmed</h1>
    </div>
    <div class="content">
      <p>Dear {{guestName}},</p>
      <p>Your RSVP for <strong>{{eventName}}</strong> has been confirmed!</p>
      <p><strong>Event Details:</strong></p>
      <ul>
        <li>Date: {{eventDate}}</li>
        <li>Location: {{eventLocation}}</li>
        <li>Cost: £{{eventCost}}</li>
      </ul>
      <p>We look forward to seeing you!</p>
    </div>
    <div class="footer">
      <p>&copy; {{year}} Dining Steward. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
    `,
    variables: ['guestName', 'eventName', 'eventDate', 'eventLocation', 'eventCost', 'year'],
  },
  {
    name: 'Payment Receipt',
    subject: 'Payment Received: {{eventName}}',
    htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background-color: #10B981; color: #fff; padding: 20px; text-align: center; }
    .content { background-color: #f9f9f9; padding: 30px; border: 1px solid #ddd; }
    .receipt { background-color: #fff; padding: 20px; margin: 20px 0; border: 1px solid #ddd; }
    .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
    h1 { margin: 0; font-size: 24px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Payment Received</h1>
    </div>
    <div class="content">
      <p>Dear {{guestName}},</p>
      <p>Thank you! We've received your payment for <strong>{{eventName}}</strong>.</p>
      
      <div class="receipt">
        <h3>Receipt</h3>
        <p><strong>Amount:</strong> £{{amount}}</p>
        <p><strong>Date:</strong> {{paymentDate}}</p>
        <p><strong>Reference:</strong> {{transactionId}}</p>
      </div>
      
      <p>Your ticket is confirmed. We look forward to seeing you!</p>
    </div>
    <div class="footer">
      <p>&copy; {{year}} Dining Steward. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
    `,
    variables: ['guestName', 'eventName', 'amount', 'paymentDate', 'transactionId', 'year'],
  },
];

export default function EmailTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templatesSnapshot = await getDocs(collection(db, 'emailTemplates'));
        const templateData: EmailTemplate[] = [];
        templatesSnapshot.forEach((doc) => {
          const data = doc.data();
          templateData.push({
            id: doc.id,
            name: data.name,
            subject: data.subject,
            htmlContent: data.htmlContent,
            variables: data.variables || [],
            lastUpdated: data.lastUpdated?.toDate() || new Date(),
          });
        });

        // If no templates exist, use defaults
        if (templateData.length === 0) {
          setTemplates(DEFAULT_TEMPLATES.map((t, i) => ({ ...t, id: `default-${i}`, lastUpdated: new Date() })));
        } else {
          setTemplates(templateData);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setTemplates(DEFAULT_TEMPLATES.map((t, i) => ({ ...t, id: `default-${i}`, lastUpdated: new Date() })));
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      if (selectedTemplate.id.startsWith('default-')) {
        // Create new template
        await addDoc(collection(db, 'emailTemplates'), {
          ...selectedTemplate,
          lastUpdated: serverTimestamp(),
        });
      } else {
        // Update existing template
        await updateDoc(doc(db, 'emailTemplates', selectedTemplate.id), {
          subject: selectedTemplate.subject,
          htmlContent: selectedTemplate.htmlContent,
          lastUpdated: serverTimestamp(),
        });
      }

      showSuccess('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="mt-2 text-3xl font-bold text-foreground">Email Templates</h1>
            <p className="text-muted-foreground">
              Customize email templates for your platform
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Template List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>Templates</CardTitle>
                  <CardDescription>Select a template to edit</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <Button
                        key={template.id}
                        variant={selectedTemplate?.id === template.id ? 'default' : 'outline'}
                        className="w-full justify-start"
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        {template.name}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Template Editor */}
            <div className="lg:col-span-2">
              {selectedTemplate ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{selectedTemplate.name}</CardTitle>
                        <CardDescription>Edit email template</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
                          <Eye className="mr-2 h-4 w-4" />
                          {showPreview ? 'Hide' : 'Show'} Preview
                        </Button>
                        <Button onClick={handleSaveTemplate} disabled={saving}>
                          <Save className="mr-2 h-4 w-4" />
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Subject */}
                      <div>
                        <Label htmlFor="subject">Email Subject</Label>
                        <Input
                          id="subject"
                          value={selectedTemplate.subject}
                          onChange={(e) =>
                            setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })
                          }
                          placeholder="Email subject line"
                        />
                      </div>

                      {/* Variables */}
                      <div>
                        <Label>Available Variables</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedTemplate.variables.map((variable) => (
                            <Badge key={variable} variant="outline">
                              {'{{' + variable + '}}'}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Use these placeholders in your template
                        </p>
                      </div>

                      {/* HTML Content */}
                      <div>
                        <Label htmlFor="htmlContent">HTML Content</Label>
                        <Textarea
                          id="htmlContent"
                          value={selectedTemplate.htmlContent}
                          onChange={(e) =>
                            setSelectedTemplate({
                              ...selectedTemplate,
                              htmlContent: e.target.value,
                            })
                          }
                          rows={20}
                          className="font-mono text-sm"
                        />
                      </div>

                      {/* Preview */}
                      {showPreview && (
                        <div>
                          <Label>Preview</Label>
                          <div className="mt-2 border rounded-lg overflow-hidden">
                            <iframe
                              srcDoc={selectedTemplate.htmlContent}
                              className="w-full h-96"
                              title="Email Preview"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Mail className="mx-auto h-12 w-12 mb-4 opacity-20" />
                    <p>Select a template to start editing</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

